"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Menu } from "lucide-react";
import { DesktopNavLinks } from "@/components/layout/DesktopNavLinks";
import { MobileNavOverlay } from "@/components/layout/MobileNavOverlay";
import { NavDonateButton } from "@/components/layout/NavDonateButton";
import { NavLogo } from "@/components/layout/NavLogo";
import { NavPill } from "@/components/layout/NavPill";
import {
  getNavLightText,
  getNavOverlay,
  type NavAccentContext,
} from "@/components/layout/nav-types";
import { useTheme } from "@/components/theme/ThemeProvider";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { useScrollContext } from "@/components/motion/SmoothScrollProvider";
import { useMobileNavOpen } from "@/components/layout/MobileNavOpenContext";
import { SyncHeaderOffset } from "@/components/layout/SyncHeaderOffset";
import { cn } from "@/lib/utils";

export function Navbar() {
  const pathname = usePathname();
  const { theme } = useTheme();
  const { scrollY } = useScrollContext();
  const [open, setOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { setMobileNavOpen } = useMobileNavOpen();
  const reduced = useReducedMotion();

  const isCharityHome = pathname === "/";
  const isLight = theme === "light";
  const isProgramsRoute = pathname.startsWith("/programs");
  const useProgramsNav = isLight && isProgramsRoute && !isCharityHome;
  const useCharityNav = isCharityHome;
  const charityNavDark = useCharityNav && !isLight;
  const isScrolled = scrollY > 48;

  const navContext: NavAccentContext = {
    charityNavDark,
    useCharityNav,
    useProgramsNav,
    isLight,
  };

  const lightText = getNavLightText(navContext, isScrolled);
  const navOverlay = getNavOverlay(navContext, isScrolled);

  const donateVariant =
    useCharityNav ? "accent" : isLight && useProgramsNav ? "green" : "accent";

  const themeToggleVariant =
    lightText || !isLight
      ? "charity-dark"
      : useCharityNav || useProgramsNav
        ? "programs-light"
        : "default";

  const themeToggleOverHero = useCharityNav && !isScrolled;

  useEffect(() => {
    setMobileNavOpen(open);
  }, [open, setMobileNavOpen]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const closeIfDesktop = () => {
      if (mq.matches) setOpen(false);
    };
    closeIfDesktop();
    mq.addEventListener("change", closeIfDesktop);
    return () => mq.removeEventListener("change", closeIfDesktop);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        ref={headerRef}
        initial={reduced ? false : { opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none fixed inset-x-0 top-0 z-50"
      >
        <SyncHeaderOffset targetRef={headerRef} />
        <div className="pointer-events-auto mx-auto w-full max-w-7xl px-4 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6 sm:pt-[max(1rem,env(safe-area-inset-top))]">
          <NavPill isScrolled={isScrolled} overlay={navOverlay}>
            <NavLogo
              {...navContext}
              isScrolled={isScrolled}
              onNavigate={() => setOpen(false)}
            />

            <DesktopNavLinks {...navContext} isScrolled={isScrolled} />

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <div className="hidden lg:block">
                <NavDonateButton
                  variant={donateVariant}
                  label={useCharityNav ? "Donate Now" : undefined}
                />
              </div>
              <ThemeToggle
                variant={themeToggleVariant}
                className={
                  themeToggleOverHero
                    ? "border-white/45 bg-black/30 text-white shadow-md backdrop-blur-md hover:bg-black/40"
                    : undefined
                }
              />
              <button
                ref={menuButtonRef}
                type="button"
                id="mobile-menu-button"
                className={cn(
                  "rounded-full p-2 lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  lightText
                    ? "text-white hover:bg-white/10"
                    : "text-gray-800 hover:bg-black/5"
                )}
                onClick={() => setOpen((prev) => !prev)}
                aria-label={open ? "Close menu" : "Open menu"}
                aria-expanded={open}
                aria-controls="mobile-nav-menu"
              >
                <Menu size={22} aria-hidden />
              </button>
            </div>
          </NavPill>
        </div>
      </motion.header>

      <MobileNavOverlay
        open={open}
        onClose={() => setOpen(false)}
        menuButtonRef={menuButtonRef}
        donateVariant={donateVariant}
        donateLabel={useCharityNav ? "Donate Now" : undefined}
      />
    </>
  );
}
